import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { MediaOptions } from 'js/src/client';
import { MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';

/**
 * Gets scene text from text assets as fallback when transcript is empty
 */
export function getSceneTextFallback(scene: Scene): string {
    const textAssets = scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((asset) => asset.isEditable);
    return textAssets
        .map((asset) => asset.text || '')
        .join(' ')
        .trim();
}

/**
 * Gets scene transcript with fallback to text assets
 */
export function getSceneText(scene: Scene): string {
    const transcript = scene.extractTranscript().text;
    return transcript || getSceneTextFallback(scene);
}

/**
 * Formats media recommendation preferences for LLM context. Emits the enabled
 * media sources, the user's media guidance (verbatim), and — when `musicMode` is
 * supplied — the music preference. Sources that are off are simply omitted from the
 * list, so the agent should treat anything not listed as unavailable. Callers are
 * responsible for resolving which guidance string to pass (e.g. falling back to
 * workspace guidance); this function renders whatever `media_guidance` it is given.
 */
export function formatMediaPreferencesForLLM(
    mediaOptions: MediaOptions,
    musicMode?: MusicRecommendationModeEnum,
): string {
    const lines: string[] = [];

    // Media sources
    const enabledSources: string[] = [];
    if (mediaOptions.stock_media_enabled) enabledSources.push('Stock media');
    if (mediaOptions.stock_icons_enabled) enabledSources.push('Stock icons');
    if (mediaOptions.dam_media_enabled) enabledSources.push('DAM media (see available media)');
    if (mediaOptions.workspace_media_mode !== WorkspaceMediaModeEnum.OFF) {
        enabledSources.push('Previous uploads (see available media)');
    }

    if (enabledSources.length > 0) {
        lines.push(`**Enabled media sources**: ${enabledSources.join(', ')}`);
    } else {
        lines.push('**Enabled media sources**: None (working with available media only)');
    }

    // Media guidance
    if (mediaOptions.media_guidance && mediaOptions.media_guidance.trim()) {
        lines.push(
            `**Media guidance (try to find media that matches this)**: ${mediaOptions.media_guidance.trim()}`,
        );
    }

    if (musicMode === MusicRecommendationModeEnum.OFF) {
        lines.push('**Music**: off — do not add background music');
    } else if (musicMode === MusicRecommendationModeEnum.STARRED) {
        lines.push("**Music**: use one of the team's starred tracks");
    }

    return lines.join('\n');
}
